import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import type { UserInterview } from '@axiom/validation';
import {
  Badge,
  Banner,
  Button,
  CalendarTimeZoneUtil,
  DateUtil,
  Flashy,
  Form,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  SmallHeader,
  useApi,
} from '@axiom/ui';

import { AccountsApi } from '../../api/accounts-api';
import { CalendarConversationsApi } from '../../api/calendar-conversations-api';
import { InterviewAttendeesAndLinkModalSchema } from '../../schema/interview-attendees-and-link-modal-schema';
import { InterviewAttendeesAndLinkModalUtil } from '../../utils/interview-attendees-and-link-modal-util';
import { InterviewAttendeesAndLinkModalFormBody } from '../InterviewAttendeesAndLinkModalFormBody/InterviewAttendeesAndLinkModalFormBody';

export type InterviewAttendeesAndLinkModalEditProps = {
  interview: UserInterview;
  onClose: () => void;
  onSave: () => Promise<void>;
};

export const InterviewAttendeesAndLinkModalEdit = ({
  interview,
  onClose,
  onSave,
}: InterviewAttendeesAndLinkModalEditProps) => {
  const [{ data: account }] = useApi(
    AccountsApi.readAccount(interview.accountId)
  );
  const accountContacts = account.contacts;
  const talentName =
    interview.candidateOpportunity.candidate.calculatedDisplayName;
  const conversation = interview.calendarConversation;
  const invitees = conversation?.invitees ?? [];
  // When the interview was scheduled without a meeting link (e.g. AFC client
  // unchecked auto-Zoom), lock the edit flow to MANUAL on the first edit.
  // Once a manual link is saved the type is no longer null and Zoom becomes
  // available on subsequent edits.
  const zoomDisabled = conversation?.meetingLinkType == null;
  const talentIana =
    interview.candidateOpportunity.candidate.calendar?.timezone;
  const conversationStart = conversation?.startTime;
  // Resolve abbr/name from the interview's actual date so the banner label
  // matches the DST in effect *then* (PST vs PDT, EST vs EDT) instead of
  // "now". Falls back to today-relative resolution if startTime is missing.
  const timezone =
    (conversationStart
      ? CalendarTimeZoneUtil.getZoneFromMomentZone(
          moment.tz(conversationStart, talentIana ?? moment.tz.guess())
        )
      : null) ?? CalendarTimeZoneUtil.getTimeZone(talentIana);

  const initialValues = useMemo(() => {
    const contactIds = invitees.flatMap(i => (i.contact ? [i.contact.id] : []));
    const adHocContacts = invitees
      .flatMap(i =>
        i.adhocAttendee
          ? [
              {
                firstName: i.adhocAttendee.firstName ?? '',
                lastName: i.adhocAttendee.lastName ?? '',
                email: i.adhocAttendee.email ?? '',
                readonly: true,
              },
            ]
          : []
      )
      // Sort existing ad-hoc attendees alphabetically so a long list stays
      // scannable. Matches the contact dropdown's fullName ordering. Newly
      // added rows are appended after these and keep their entry order.
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`
          .trim()
          .localeCompare(`${b.firstName} ${b.lastName}`.trim())
      );
    const meetingLinkType: 'ZOOM' | 'MANUAL' =
      conversation?.meetingLinkType === 'ZOOM' ? 'ZOOM' : 'MANUAL';
    // The text input is for MANUAL links only. When the conversation is
    // ZOOM, its meetingLink carries the auto-provisioned Zoom URL — don't
    // surface that into the field, otherwise toggling MANUAL leaves the
    // user with a stale Zoom URL to clear.
    const meetingLink =
      meetingLinkType === 'MANUAL' ? (conversation?.meetingLink ?? '') : '';

    return {
      contacts: contactIds,
      adHocContacts,
      meetingLinkType,
      meetingLink,
    };
  }, [conversation, invitees]);

  return (
    <Form
      name="EDIT_ATTENDEES_FORM"
      schema={InterviewAttendeesAndLinkModalSchema}
      initialValues={initialValues}
      onSubmit={async (_changedValues, _actions, values) => {
        if (!conversation) return;

        const diff = InterviewAttendeesAndLinkModalUtil.computeAttendeeDiff(
          invitees,
          values.contacts,
          values.adHocContacts.filter(c => c.email)
        );

        const updateBody: Parameters<
          typeof CalendarConversationsApi.updateConversation
        >[1] = {};

        if (diff.toAdd.contactIds.length > 0) {
          updateBody.addContactIds = diff.toAdd.contactIds;
        }
        if (diff.toAdd.adhocAttendees.length > 0) {
          updateBody.addAdhocAttendees = diff.toAdd.adhocAttendees;
        }
        if (diff.toRemove.length > 0) {
          updateBody.removeEventIds = diff.toRemove;
        }

        const currentType = conversation.meetingLinkType ?? 'MANUAL';
        const newType = values.meetingLinkType;
        // Only treat the link as changed for MANUAL. For ZOOM the form keeps
        // meetingLink empty while the conversation stores the auto-provisioned
        // Zoom URL, so comparing the two always reports a phantom change — which
        // fired a no-op PATCH (`{ meetingLinkType: 'ZOOM', meetingUrl: null }`)
        // on every unchanged save of an already-Zoom interview. The backend
        // short-circuits ZOOM→ZOOM anyway, so suppress the request entirely.
        const typeChanged = newType !== currentType;
        const linkChanged =
          newType === 'MANUAL' &&
          values.meetingLink !== (conversation.meetingLink ?? '');
        if (typeChanged || linkChanged) {
          updateBody.meetingLinkType = newType;
          updateBody.meetingUrl =
            newType === 'MANUAL' ? values.meetingLink : null;
        }

        if (Object.keys(updateBody).length > 0) {
          await CalendarConversationsApi.updateConversation(
            conversation.id,
            updateBody
          );
        }

        onClose();
        await onSave();
      }}
    >
      {({ values, setValues, fireSubmit }) => (
        <Modal name="EDIT_ATTENDEES_MODAL" size="large">
          <ModalHeader
            name="EDIT_ATTENDEES_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            <Layout position="middle" wrap>
              <LayoutItem fluid>
                <SmallHeader name="HEADER_TEXT">
                  Attendees and meeting link for the interview with {talentName}
                </SmallHeader>
              </LayoutItem>
              <LayoutItem>
                <Badge
                  name="SCHEDULE_INTERVIEW_BADGE"
                  background="forest green"
                >
                  Schedule interview
                </Badge>
              </LayoutItem>
            </Layout>
          </ModalHeader>
          <ModalSection>
            <Gutter bottom="16px">
              <Banner name="INTERVIEW_BANNER">
                <Flashy bold color="controlSecondary">
                  Interview{' '}
                </Flashy>
                {DateUtil.displayBannerTimestamp(
                  conversation?.startTime,
                  conversation?.endTime,
                  timezone
                )}{' '}
                {timezone.abbr} {timezone.name}
              </Banner>
            </Gutter>
            <InterviewAttendeesAndLinkModalFormBody
              accountId={interview.accountId}
              accountContacts={accountContacts}
              talentName={talentName ?? 'Talent'}
              values={values}
              setValues={setValues}
              zoomDisabled={zoomDisabled}
            />
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_BUTTON"
              onClick={() => {
                onClose();
              }}
              pattern="primary"
              variation="outline"
            >
              Cancel
            </Button>
            <Button name="SAVE_BUTTON" onClick={fireSubmit}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
